import { useState, useEffect, useCallback } from "react";
import { Box, Button, Dialog, DialogActions, DialogTitle, Slide, Stack, Tooltip } from "@mui/material";
import Header from "../../components/Header";
import DebFormModal from "../../components/DebFormModal";
import {
    DebDatePickerInput,
    DebFormListener,
    DebFormMultiSelect,
    DebFormSelect,
    DebFormTextInput,
} from "../../components/DebFormComponents";
import DataTable from "../../components/DataTable";
import { license } from "../../services/licenses";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { company } from "../../services/companies";
import { branch } from "../../services/branches";
import { format, isValid } from "date-fns";
import { licenseValidationSchema } from "../../schemas/licenses";


//TRANSICIÓN DEL ALERT DE ELIMINACIÓN
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  
const newLicenseValues = {
    system: "",
    expired_date: null,
    details: "",
    company: {
        id: "",
    },
    branchesList: [],
};

const Licenses = () => {
    // MODAL DE EDICIÓN / CREACIÓN
    const [modalState, setModalState] = useState(false);
    const [modalInitialValues, setModalInitialValues] =
        useState(newLicenseValues);

    const [licenses, setLicenses] = useState([]);
    const getLicenses = async () => {
        setLicenses(await license.getAll());
    };

    const [companies, setCompanies] = useState([]);
    const getCompanies = async () => {
        setCompanies(await company.getAll());
    };

    const [branches, setBranches] = useState([]);
    const getBranches = async () => {
        setBranches(await branch.getAll());
    };

    const [companyBranches, setCompanyBranches] = useState([]);

    useEffect(() => {
        getLicenses();
        getCompanies();
        getBranches();
    }, []);

    const { enqueueSnackbar } = useSnackbar();

    const modifiedLicenses = [...licenses].map((license) => ({
        ...license,
        companyName: license.company.name,
        branches: license.branchesList.map((branch) => branch.name).join(', ')
    }));

    const handleSubmit = async (values) => {
        values.branchesList = values.branchesList.map((id) => {
            return { id: id };
        });
        try {
            if (values.id) {
                //estamos editando
                const res = await license.update(values);
                enqueueSnackbar("Licencia editada", {
                    preventDuplicate: true,
                    variant: "success",
                });
            } else {
                const res = await license.create(values);
                enqueueSnackbar("Licencia creada", {
                    preventDuplicate: true,
                    variant: "success",
                });
            }
            closeModal();
            getLicenses();
        } catch (error) {
            if (values.id) {
                enqueueSnackbar("Ocurrió un error al editar la licencia", {
                    preventDuplicate: true,
                    variant: "error",
                });
            } else {
                enqueueSnackbar("Ocurrió un error al crear la licencia.", {
                    preventDuplicate: true,
                    variant: "error",
                });
                error.response.status === 500 && 
                enqueueSnackbar("Verifique que no exista ya una licencia para la sucursal seleccionada.", {
                    preventDuplicate: true,
                    variant: "error",
                }); 
                
            }
        }
    };

    const openModal = () => {
        setModalState(true);
    };

    const closeModal = () => {
        setModalState(false);
        setModalInitialValues(newLicenseValues);
    };

    const handleEdit = (license) => {
        license.branchesList = license.branchesList.map((branch) => branch.id);
        setModalInitialValues(license);
        openModal();
    };

    const handleCreateLicense = () => {
        setModalInitialValues(newLicenseValues);
        openModal();
    };


    const getCompanyIdFromBranchId = (branchId) => {
        const branch = branches.filter((branch) => {
            return branch.id === branchId;
        })[0];
        return branch.company.id;
    };

    const handleFormChange = useCallback(
        (values) => {
            // aca llenamos la lista de companias
            if (
                !values.company ||
                (values.branchesList.length > 0 &&
                    values.company.id !==
                        getCompanyIdFromBranchId(values.branchesList[0]))
            ) {
                values.branchesList = [];
                setModalInitialValues(values);
            }
            setCompanyBranches(
                branches.filter((branch) => branch?.company?.id && values?.company?.id
                    && branch?.company?.id === values?.company?.id )
            );
        },
        [branches]
    );

     // ALERT DE ELIMINACIÓN
        const [selectedDelete, setSelectedDelete] = useState();
        const [openAlert, setOpenAlert] = useState(false);
        const handleAlertClose = () => {
            setOpenAlert(false);
        };
        const handleAlertOpen = (selectedLicense) => {
            setSelectedDelete(selectedLicense);
            setOpenAlert(true);
        };

    const handleDelete = async () => {
        try {
            const res = await license.delete(selectedDelete.id);
            enqueueSnackbar("Se eliminó la licencia " + selectedDelete.system, {
                preventDuplicate: true,
                variant: "success",
            });
            handleAlertClose();
            getLicenses();
        } catch (error) {
            enqueueSnackbar("Ocurrió un error eliminando la licencia", {
                preventDuplicate: true,
                variant: "error",
            });
        }
    };

    return (
        <Box>
            <Header title="LICENCIAS" subtitle="Lista de Licencias" />
            <DataTable
                loading={!licenses.length}
                rows={modifiedLicenses}
                columns={[
                    { field: "id", headerName: "ID", flex: 0.1 },
                    {
                        field: "companyName",
                        headerName: "Compañía",
                        flex: 1,
                        renderCell: (params) => {
                            return params?.row?.companyName ?? "";
                        },
                    },
                    {
                        field: "branches",
                        headerName: "Sucursales",
                        flex: 1,
                        renderCell: (params) => {
                            return (
                                <Tooltip title={params.row.branches} arrow>
                                    <span
                                        style={{
                                            textOverflow: "ellipsis",
                                            minWidth: "0px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                        }}>
                                        {params.row.branches}
                                    </span>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        field: "system",
                        headerName: "Sistema",
                        flex: 1,
                        renderCell: (params) => (
                            <Tooltip title={params?.value ?? ""} arrow>
                                <span className="table-cell-trucate">
                                    {params?.value ?? ""}
                                </span>
                            </Tooltip>
                        ),
                    },
                    {
                        field: "expired_date",
                        headerName: "Fecha de Expiración",
                        renderCell: (params) => {
                            const date  = new Date(params?.value ?? "");
                            if(isValid(date)) {
                                return format(date, "dd/MM/yyyy");
                            } else {
                                return "";
                            }
                        },
                        flex: 1,
                    },
                    {
                        field: "details",
                        headerName: "Detalles",
                        flex: 1,
                        renderCell: (params) => (
                            <Tooltip title={params?.value ?? ""} arrow>
                                <span className="table-cell-trucate">
                                    {params?.value ?? ""}
                                </span>
                            </Tooltip>
                        ),
                    },
                ]}
                rowActions={[
                    {
                        label: "Editar",
                        icon: <EditIcon />,
                        action: handleEdit,
                    },
                    {
                        label: "Eliminar",
                        icon: <DeleteIcon />,
                        action: handleAlertOpen,
                        showInMenu: true,
                    },
                ]}
                headerActions={[
                    {
                        label: "Crear Licencia",
                        icon: <AddBoxIcon />,
                        action: handleCreateLicense,
                    },
                ]}
            />

            {/* MODAL DE CREACIÓN / EDICIÓN DE COMPAÑÍA */}
            <DebFormModal
                maxWidth="sm"
                fullWidth={true}
                open={modalState}
                onClose={closeModal}
                onReject={closeModal}
                initialValues={modalInitialValues}
                onSubmit={handleSubmit}
                formikProps={{
                    enableReinitialize: true,
                    validationSchema: licenseValidationSchema,
                }}
                headerText={
                    modalInitialValues?.id
                        ? "Editar Licencia"
                        : "Crear Licencia"
                }>
                <Stack spacing={1}>
                    <DebFormSelect
                        label={"Compañía"}
                        name={"company.id"}
                        selectOptions={Array.from(companies)?.map((company) => {
                            return {
                                value: company.id,
                                label: company.name,
                            };
                        })}
                    />
                    <DebFormSelect
                        label={"Sistema"}
                        name={"system"}
                        selectOptions={[
                            //Se comentan Citas y Mobile por el momento
                            //{ label: "Citas", value: "APPOINTMENTS" },
                            //{ label: "Mobile", value: "MOBILE" },
                            { label: "Encuestas", value: "SURVEY" },
                        ]}
                    />
                    <DebFormTextInput label={"Detalles"} name={"details"} />
                    <DebDatePickerInput
                        label={"Fecha de expiración"}
                        name={"expired_date"}
                    />
                    <DebFormMultiSelect
                        label={"Sucursales asociadas"}
                        name={"branchesList"}
                        selectOptions={companyBranches.map((companyBranch) => {
                            return {
                                value: companyBranch.id,
                                label: companyBranch.name,
                            };
                        })}
                    />
                    <DebFormListener otraCosa={handleFormChange} />
                </Stack>
            </DebFormModal>
                    
            {/* ALERT DE ELIMINACIÓN */}
            <Dialog
                open={openAlert}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleAlertClose}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle sx={{m:"2rem 3rem 1rem 3rem", gap:"1rem", p:"0"}} >{"¿Esta seguro que desea eliminar?"}</DialogTitle>
                <DialogActions>
                <Button onClick={handleAlertClose} variant="outlined" color="error">Cancelar</Button>
                <Button onClick={handleDelete} variant="contained" color="success">Confirmar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Licenses;
